import {
  CreatePlayerSessionResponse,
  PLAYER_SESSION_PROTOCOL_VERSION,
  PLAYER_SESSION_RENEWAL_WINDOW_SECONDS,
} from "@admefy/domain";
import { HttpBackend, HttpClient, HttpErrorResponse, HttpHeaders } from "@angular/common/http";
import { inject, Injectable, signal } from "@angular/core";
import { WA_WINDOW } from "@ng-web-apis/common";
import {
  catchError,
  defer,
  finalize,
  from,
  map,
  Observable,
  of,
  shareReplay,
  Subject,
  switchMap,
  tap,
  timer,
  throwError,
} from "rxjs";
import { Token } from "../../models/token.model";
import { ApiURL } from "../../tokens";
import { LocalStorageJwtService } from "../token-storage/local-storage-jwt.service";
import { PlayerInstanceIdentityService } from "./player-instance-identity.service";

@Injectable({ providedIn: "root" })
export class PlayerSessionService {
  private readonly apiUrl = inject(ApiURL);
  private readonly bootstrapStorage = inject(LocalStorageJwtService);
  private readonly identity = inject(PlayerInstanceIdentityService);
  private readonly window = inject(WA_WINDOW);
  private readonly http = new HttpClient(inject(HttpBackend));
  private readonly accessToken = signal<Token | null>(null);
  private readonly renewedSubject = new Subject<string>();
  private inFlight?: Observable<string | null>;
  private renewalTimer?: number;
  private renewalRetryAttempt = 0;
  private nextRetryAt = 0;
  private legacyMode = false;

  readonly renewed$ = this.renewedSubject.asObservable();

  currentAccessToken(): string | null {
    const token = this.accessToken();
    return token?.isValid() ? token.toString() : null;
  }

  isLegacyMode(): boolean {
    return this.legacyMode;
  }

  ensureValidSession(force = false): Observable<string | null> {
    if (this.legacyMode) return of(null);
    const current = this.accessToken();
    if (!force && current?.isValid()) return of(current.toString());
    if (this.inFlight) return this.inFlight;
    const retryDelay = this.nextRetryAt - Date.now();
    if (retryDelay > 0) {
      return timer(retryDelay).pipe(switchMap(() => this.ensureValidSession(force)));
    }

    this.inFlight = defer(() => from(this.identity.ready)).pipe(
      switchMap(() => {
        const bootstrap = this.bootstrapStorage.getItemSync();
        if (!bootstrap?.isValid()) return of(null);
        return this.http.post<{ data?: CreatePlayerSessionResponse } | CreatePlayerSessionResponse>(
          `${this.apiUrl}/session`,
          {
            instanceId: this.identity.value,
            protocolVersion: PLAYER_SESSION_PROTOCOL_VERSION,
          },
          {
            headers: new HttpHeaders({ Authorization: `Bearer ${bootstrap.toString()}` }),
          },
        );
      }),
      map((response) => {
        if (!response) return null;
        return "accessToken" in response ? response : (response.data ?? null);
      }),
      tap((response) => {
        if (!response?.accessToken) return;
        const token = new Token(response.accessToken);
        this.accessToken.set(token);
        this.renewalRetryAttempt = 0;
        this.nextRetryAt = 0;
        this.scheduleRenewal(token);
        this.renewedSubject.next(token.toString());
      }),
      map((response) => response?.accessToken ?? null),
      catchError((error: unknown) => {
        if (
          error instanceof HttpErrorResponse &&
          (error.status === 404 || error.status === 405 || error.status === 426)
        ) {
          this.legacyMode = true;
          this.accessToken.set(null);
          return of(null);
        }
        if (this.isRetryableRenewalError(error)) {
          this.recordRenewalFailure(error);
        }
        return throwError(() => error);
      }),
      finalize(() => {
        this.inFlight = undefined;
      }),
      shareReplay({ bufferSize: 1, refCount: false }),
    );
    return this.inFlight;
  }

  private scheduleRenewal(token: Token): void {
    if (this.renewalTimer !== undefined) this.window.clearTimeout(this.renewalTimer);
    const renewAt =
      token.getTokenExpDate().getTime() - PLAYER_SESSION_RENEWAL_WINDOW_SECONDS * 1000 - Date.now();
    this.renewalTimer = this.window.setTimeout(
      () => this.renewScheduledSession(),
      Math.max(0, renewAt),
    );
  }

  private renewScheduledSession(): void {
    this.ensureValidSession(true).subscribe({
      error: (error: unknown) => {
        if (!this.isRetryableRenewalError(error)) return;
        const delay = Math.max(0, this.nextRetryAt - Date.now());
        this.renewalTimer = this.window.setTimeout(() => this.renewScheduledSession(), delay);
      },
    });
  }

  private isRetryableRenewalError(error: unknown): boolean {
    return (
      !(error instanceof HttpErrorResponse) ||
      error.status === 0 ||
      error.status === 429 ||
      error.status >= 500
    );
  }

  private getRenewalRetryDelay(error: unknown): number {
    if (error instanceof HttpErrorResponse && error.status === 429) {
      const retryAfter = error.headers.get("Retry-After");
      if (retryAfter) {
        const seconds = Number(retryAfter);
        const retryAt = Number.isFinite(seconds)
          ? Date.now() + seconds * 1000
          : Date.parse(retryAfter);
        if (Number.isFinite(retryAt)) {
          return Math.max(1000, retryAt - Date.now());
        }
      }
    }
    return Math.min(30_000 * 2 ** this.renewalRetryAttempt, 5 * 60_000);
  }

  private recordRenewalFailure(error: unknown): void {
    const delay = this.getRenewalRetryDelay(error);
    this.renewalRetryAttempt += 1;
    this.nextRetryAt = Date.now() + delay;
  }
}
